import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@libs/zapi-ui/shadcn";
import { ConfirmationModal } from "@libs/zapi-ui/custom";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import {
  emptyDimensionForm,
  dimensionFormFromExisting,
  type TDimensionFormState
} from "../../lib/dimensionForm";
import { useManageDimensions } from "../../lib/useManageDimensions";
import { ManageDimensionsHeader } from "./_ManageDimensionsHeader";
import { DimensionsListView } from "./_DimensionsListView";
import { DimensionFormView } from "./_DimensionFormView";
import { useTranslation } from "react-i18next";

type TView = "list" | "add" | "edit";

type TProps = {
  open: boolean;
  onClose: () => void;
  orgId: number;
  dimensions: TOrganizationProjectDimension[];
};

export function ManageDimensionsModal({ open, onClose, orgId, dimensions }: TProps) {
  const { t } = useTranslation("projects");
  const [view, setView] = useState<TView>("list");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<TDimensionFormState>(emptyDimensionForm);

  const resetToList = () => {
    setView("list");
    setEditingId(null);
    setForm(emptyDimensionForm());
  };

  const {
    orderedDims,
    confirmingDeleteId,
    setConfirmingDeleteId,
    deletingId,
    isFormPending,
    isDeletePending,
    destructive,
    setDestructive,
    handleDragEnd,
    create,
    update,
    remove
  } = useManageDimensions({
    orgId,
    dimensions,
    onAfterCreate: resetToList,
    onAfterUpdate: resetToList
  });

  const handleClose = () => {
    resetToList();
    setConfirmingDeleteId(null);
    onClose();
  };

  const startEdit = (d: TOrganizationProjectDimension) => {
    setEditingId(d.id);
    setForm(dimensionFormFromExisting(d));
    setView("edit");
  };

  const startAdd = () => {
    setForm(emptyDimensionForm());
    setView("add");
  };

  const handleSave = () => {
    if (view === "add") create(form);
    else if (editingId != null) update(editingId, form);
  };

  const canSave = form.name.trim().length > 0 && (form.type === "text" || form.options.length > 0);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent
        data-testid="manage-dimensions-modal"
        className="flex max-h-[85vh] max-w-[540px] flex-col gap-0 overflow-hidden rounded-xl border-0 bg-[#fafafa] p-0 ring-1 ring-inset ring-innoveraZinc-100 [&>button]:hidden"
      >
        <DialogTitle className="sr-only">
          {view === "list"
            ? t("dimensions.heading")
            : view === "add"
              ? t("dimensions.newTitle")
              : t("dimensions.editTitle")}
        </DialogTitle>
        <div className="flex min-h-0 flex-1 flex-col">
          <ManageDimensionsHeader
            view={view}
            isPending={isFormPending}
            onBack={resetToList}
            onClose={handleClose}
          />

          {view === "list" && (
            <DimensionsListView
              dimensions={orderedDims}
              confirmingDeleteId={confirmingDeleteId}
              deletingId={deletingId}
              isDeletePending={isDeletePending}
              onDragEnd={handleDragEnd}
              onEdit={startEdit}
              onAskDelete={setConfirmingDeleteId}
              onConfirmDelete={remove}
              onCancelDelete={() => setConfirmingDeleteId(null)}
              onAddNew={startAdd}
            />
          )}

          {view !== "list" && (
            <DimensionFormView
              mode={view}
              form={form}
              isPending={isFormPending}
              canSave={canSave}
              onChange={setForm}
              onCancel={resetToList}
              onSave={handleSave}
            />
          )}
        </div>
      </DialogContent>

      <ConfirmationModal
        isOpen={destructive != null}
        onClose={() => setDestructive(null)}
        onConfirm={() => destructive?.pending()}
        title={t("dimensions.confirmChangeTitle")}
        description={
          destructive
            ? `${destructive.message} ${t("dimensions.affectedCount", { count: destructive.affectedCount })}`
            : ""
        }
        confirmText={t("dimensions.confirmChangeAction")}
      />
    </Dialog>
  );
}
